import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Eye, MessageSquare, TrendingUp, Sparkles, AlertTriangle, ChevronRight, Crown, Hash, RefreshCw } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Per-property leads dashboard. Shows reveal counts (anonymous interest) and
 * inquiry messages (high-intent leads) side-by-side, with engagement-sorted
 * cards and a tiny "actionable insight" hint when a listing has many reveals
 * but zero messages (probable description / photo / pricing issue).
 */
export default function LeadsTab() {
  const { token } = useAuth();
  const { language } = useLanguage();
  const tt = (en, es) => (language === 'es' ? es : en);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [expanded, setExpanded] = useState({});

  const fetchLeads = async ({ silent = false } = {}) => {
    if (!token) return;
    if (silent) setRefreshing(true);
    else setLoading(true);
    try {
      const r = await axios.get(`${API}/agent/leads`, { headers: { Authorization: `Bearer ${token}` } });
      setData(r.data);
    } catch {
      setData({ leads: [], summary: { total_reveals: 0, total_messages: 0, properties: 0, with_engagement: 0 } });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { fetchLeads(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [token]);

  const fmtDate = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      return d.toLocaleDateString(language === 'es' ? 'es-VE' : 'en-US', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
    } catch { return iso.slice(0, 16).replace('T', ' '); }
  };

  if (loading) {
    return (
      <div className="space-y-3" data-testid="leads-loading">
        {[1, 2, 3].map((i) => <Skeleton key={i} className="h-24 w-full rounded-xl" />)}
      </div>
    );
  }

  const leads = data?.leads || [];
  const summary = data?.summary || { total_reveals: 0, total_messages: 0, properties: 0, with_engagement: 0 };

  return (
    <div className="space-y-4" data-testid="leads-tab-root">
      <div className="flex items-center justify-end">
        <Button
          variant="outline"
          size="sm"
          onClick={() => fetchLeads({ silent: true })}
          disabled={refreshing}
          className="gap-1.5 h-8"
          data-testid="leads-refresh-btn"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
          {tt('Refresh', 'Actualizar')}
        </Button>
      </div>

      {/* Summary strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card data-testid="leads-summary-reveals">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center shrink-0"><Eye className="w-5 h-5 text-amber-700" /></div>
            <div className="min-w-0">
              <div className="text-2xl font-heading font-bold text-[#0B192C] leading-none">{summary.total_reveals}</div>
              <div className="text-xs text-slate-500 mt-1">{tt('Contact reveals', 'Vistas de contacto')}</div>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="leads-summary-messages">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center shrink-0"><MessageSquare className="w-5 h-5 text-emerald-700" /></div>
            <div className="min-w-0">
              <div className="text-2xl font-heading font-bold text-[#0B192C] leading-none">{summary.total_messages}</div>
              <div className="text-xs text-slate-500 mt-1">{tt('Inquiry messages', 'Mensajes de consulta')}</div>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="leads-summary-properties">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center shrink-0"><Hash className="w-5 h-5 text-slate-600" /></div>
            <div className="min-w-0">
              <div className="text-2xl font-heading font-bold text-[#0B192C] leading-none">{summary.properties}</div>
              <div className="text-xs text-slate-500 mt-1">{tt('Properties tracked', 'Propiedades')}</div>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="leads-summary-engaged">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/20 flex items-center justify-center shrink-0"><TrendingUp className="w-5 h-5 text-[#0B192C]" /></div>
            <div className="min-w-0">
              <div className="text-2xl font-heading font-bold text-[#0B192C] leading-none">{summary.with_engagement}</div>
              <div className="text-xs text-slate-500 mt-1">{tt('With activity', 'Con actividad')}</div>
            </div>
          </CardContent>
        </Card>
      </div>

      {leads.length === 0 ? (
        <Card data-testid="leads-empty">
          <CardContent className="p-10 text-center">
            <Eye className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-500">{tt('No leads yet — keep sharing your listings.', 'Aún no hay leads — sigue compartiendo tus propiedades.')}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {leads.map((l) => {
            const p = l.property;
            const r = l.reveals;
            const m = l.messages;
            const hasInterest = r.count > 0 || m.count > 0;
            const stalled = r.count >= 3 && m.count === 0;
            const isOpen = expanded[p.id];
            return (
              <Card key={p.id} className={hasInterest ? 'border-amber-200/70' : ''} data-testid={`lead-card-${p.id}`}>
                <CardContent className="p-4">
                  <div className="flex gap-3">
                    {p.main_photo ? (
                      <Link to={`/property/${p.id}`} className="shrink-0">
                        <img src={p.main_photo} alt={p.title} className="w-20 h-20 rounded-lg object-cover" />
                      </Link>
                    ) : (
                      <div className="w-20 h-20 rounded-lg bg-slate-100 shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <div className="min-w-0">
                          <Link to={`/property/${p.id}`} className="font-medium text-[#0B192C] hover:underline truncate block" data-testid={`lead-title-${p.id}`}>{p.title}</Link>
                          <div className="text-xs text-slate-500 flex flex-wrap items-center gap-2 mt-0.5">
                            <span className="font-mono bg-[#D4AF37]/10 text-[#0B192C] px-1.5 py-0.5 rounded">{p.wakacode}</span>
                            <span>{p.city}{p.neighborhood ? `, ${p.neighborhood}` : ''}</span>
                            {p.status !== 'approved' && <Badge variant="secondary" className="h-4 text-[10px]">{p.status}</Badge>}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <div className="inline-flex items-center gap-1 text-xs bg-amber-50 text-amber-800 border border-amber-200 px-2 py-1 rounded-md" data-testid={`lead-reveals-${p.id}`}>
                            <Eye className="w-3.5 h-3.5" /> {r.count}
                          </div>
                          <div className="inline-flex items-center gap-1 text-xs bg-emerald-50 text-emerald-800 border border-emerald-200 px-2 py-1 rounded-md" data-testid={`lead-messages-${p.id}`}>
                            <MessageSquare className="w-3.5 h-3.5" /> {m.count}
                            {m.premium > 0 && <Crown className="w-3 h-3 text-[#D4AF37] ml-0.5" />}
                          </div>
                        </div>
                      </div>

                      {/* Actionable insight */}
                      {stalled && (
                        <div className="mt-2 inline-flex items-start gap-1.5 text-[11px] bg-amber-50 border border-amber-200 text-amber-900 px-2 py-1 rounded-md" data-testid={`lead-insight-${p.id}`}>
                          <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
                          <span>
                            {tt(
                              `${r.count} reveals but no messages — consider improving photos, description or price.`,
                              `${r.count} vistas pero sin mensajes — mejora fotos, descripción o precio.`
                            )}
                          </span>
                        </div>
                      )}
                      {!stalled && r.count > 0 && m.count > 0 && (
                        <div className="mt-2 inline-flex items-center gap-1 text-[11px] text-emerald-700">
                          <Sparkles className="w-3 h-3" />
                          {tt('Healthy engagement', 'Buen engagement')}
                        </div>
                      )}

                      {hasInterest && (
                        <button
                          type="button"
                          onClick={() => setExpanded(e => ({ ...e, [p.id]: !e[p.id] }))}
                          className="mt-2 text-xs text-[#0B192C] hover:text-[#D4AF37] inline-flex items-center gap-0.5"
                          data-testid={`lead-toggle-${p.id}`}
                        >
                          {isOpen ? tt('Hide details', 'Ocultar detalles') : tt('Show recent activity', 'Ver actividad reciente')}
                          <ChevronRight className={`w-3.5 h-3.5 transition ${isOpen ? 'rotate-90' : ''}`} />
                        </button>
                      )}

                      {isOpen && (
                        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid={`lead-details-${p.id}`}>
                          {/* Recent reveals */}
                          <div className="border border-amber-200 rounded-lg p-2 bg-amber-50/30">
                            <h4 className="text-[11px] font-semibold uppercase tracking-wider text-amber-800 flex items-center gap-1 mb-1.5">
                              <Eye className="w-3 h-3" /> {tt('Recent reveals', 'Vistas recientes')}
                            </h4>
                            {r.recent.length === 0 ? (
                              <p className="text-[11px] text-slate-500">{tt('None yet', 'Ninguna aún')}</p>
                            ) : (
                              <ul className="space-y-1">
                                {r.recent.map((rv, i) => (
                                  <li key={i} className="text-[11px] text-slate-700 flex items-center justify-between gap-2">
                                    <span className="truncate">
                                      {rv.actor_name ? <span className="font-medium">{rv.actor_name}</span> : <span className="italic text-slate-500">{tt('Anonymous', 'Anónimo')}</span>}
                                      {rv.click_count > 1 && <span className="ml-1 text-slate-400">×{rv.click_count}</span>}
                                    </span>
                                    <span className="text-slate-500 shrink-0">{fmtDate(rv.last_revealed_at)}</span>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>

                          {/* Recent messages */}
                          <div className="border border-emerald-200 rounded-lg p-2 bg-emerald-50/30">
                            <h4 className="text-[11px] font-semibold uppercase tracking-wider text-emerald-800 flex items-center gap-1 mb-1.5">
                              <MessageSquare className="w-3 h-3" /> {tt('Recent messages', 'Mensajes recientes')}
                            </h4>
                            {m.recent.length === 0 ? (
                              <p className="text-[11px] text-slate-500">{tt('None yet', 'Ninguno aún')}</p>
                            ) : (
                              <ul className="space-y-1.5">
                                {m.recent.map((msg, i) => (
                                  <li key={i} className="text-[11px] text-slate-700 border-b border-emerald-100 last:border-b-0 pb-1.5 last:pb-0">
                                    <div className="flex items-center justify-between gap-2">
                                      <span className="font-medium truncate inline-flex items-center gap-1">
                                        {msg.sender_name || tt('Visitor', 'Visitante')}
                                        {msg.sender_buyer_tier === 'premium' && <Crown className="w-3 h-3 text-[#D4AF37]" />}
                                      </span>
                                      <span className="text-slate-500 shrink-0">{fmtDate(msg.created_at)}</span>
                                    </div>
                                    {msg.message && <p className="text-slate-600 line-clamp-2 mt-0.5">{msg.message}</p>}
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
